import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck, Users, IndianRupee, Cpu, TrendingUp, Wallet, X,
  AlertTriangle, RefreshCw, Search, Gift,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GlassCard, StageHeader, Pill, Spinner, cx } from "@/components/shared";

const INR = (n) => "\u20B9" + Number(n || 0).toLocaleString("en-IN");
const NUM = (n) => Number(n || 0).toLocaleString("en-IN");

function Metric({ label, value, sub, icon: Icon, tone = "purple" }) {
  const c = tone === "gold" ? "#FFB300" : tone === "green" ? "#34d399" : tone === "red" ? "#f87171" : "#5B4DFF";
  return (
    <GlassCard className="p-5 relative overflow-hidden" data-testid={`admin-metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="absolute -bottom-6 -right-6 w-20 h-20 rounded-full blur-2xl opacity-30" style={{ background: c }} />
      <div className="relative flex items-center justify-between">
        <div className="font-mono text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
        {Icon && <Icon className="w-4 h-4" style={{ color: c }} />}
      </div>
      <div className="relative mt-2 font-display text-2xl sm:text-3xl font-bold text-white">{value}</div>
      {sub && <div className="relative mt-1 text-xs text-slate-500">{sub}</div>}
    </GlassCard>
  );
}

function Breakdown({ title, rows, kind = "count" }) {
  return (
    <GlassCard className="p-5" data-testid={`admin-breakdown-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="font-mono text-[11px] uppercase tracking-wider text-slate-400 mb-3">{title}</div>
      <div className="space-y-2">
        {(rows || []).length === 0 && <div className="text-sm text-slate-500">No data in range.</div>}
        {(rows || []).map((r) => (
          <div key={r.key} className="flex items-center justify-between text-sm">
            <span className="text-slate-300 truncate">{r.key}</span>
            <span className="text-slate-400 font-mono">
              {r.count} {kind === "credits" && r.credits ? `· ${NUM(r.credits)} cr` : ""}
              {r.cost_usd ? ` · $${r.cost_usd}` : ""}
            </span>
          </div>
        ))}
      </div>
    </GlassCard>
  );
}

function GiftCredits({ email, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(null);
  const submit = async () => {
    const credits = parseInt(amount, 10);
    if (!credits || credits <= 0) { setErr("Enter a positive credit amount."); return; }
    if (!reason.trim()) { setErr("A reason is required for the audit record."); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/credits/grant", { email, credits, reason: reason.trim() });
      setOk(data.balance);
      onDone && onDone();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Grant failed.");
    } finally { setBusy(false); }
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="admin-gift-modal">
      <motion.div initial={{ y: 24, scale: 0.98 }} animate={{ y: 0, scale: 1 }} exit={{ y: 24, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl border border-white/12 bg-[#12121f]/97 p-6 shadow-2xl">
        <div className="flex items-center gap-2.5 mb-1">
          <span className="grid place-items-center w-9 h-9 rounded-xl bg-[#5B4DFF]/15 border border-[#5B4DFF]/40 text-[#b6aaff]"><Gift className="w-4.5 h-4.5" /></span>
          <div className="font-display text-lg font-semibold text-white">Gift credits</div>
          <button onClick={onClose} className="ml-auto p-2 rounded-lg hover:bg-white/10 text-slate-400" data-testid="admin-gift-close"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-slate-400 mb-4">Recorded as an <b className="text-[#b6aaff]">ADMIN_GRANT</b> — ₹0 customer revenue, fully audited. Not counted as a sale.</p>
        {ok !== null ? (
          <div className="text-center py-4" data-testid="admin-gift-success">
            <div className="text-emerald-300 font-semibold">Granted to {email}</div>
            <div className="text-sm text-slate-400 mt-1">New balance: <span className="text-[#FFB300] font-mono">{ok.toLocaleString()}</span></div>
            <button onClick={onClose} className="mt-4 px-4 py-2 rounded-xl bg-[#5B4DFF] text-white text-sm font-semibold">Done</button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Recipient</label>
              <div className="text-sm text-white mt-1" data-testid="admin-gift-email">{email}</div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Credit amount</label>
              <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ""))} inputMode="numeric"
                data-testid="admin-gift-amount" placeholder="e.g. 4000"
                className="w-full mt-1 bg-white/[0.05] border border-white/12 rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-[#5B4DFF]/50" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Reason (audited)</label>
              <input value={reason} onChange={(e) => setReason(e.target.value)}
                data-testid="admin-gift-reason" placeholder="e.g. goodwill / support compensation"
                className="w-full mt-1 bg-white/[0.05] border border-white/12 rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-[#5B4DFF]/50" />
            </div>
            {err && <div className="text-sm text-red-400" data-testid="admin-gift-error">{err}</div>}
            <button onClick={submit} disabled={busy} data-testid="admin-gift-submit"
              className="w-full py-2.5 rounded-xl bg-[#5B4DFF] hover:bg-[#4a3ff0] text-white text-sm font-semibold transition disabled:opacity-50">
              {busy ? "Granting…" : "Confirm grant"}
            </button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}


function UserInvestigation({ uid, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/user/${uid}`).then(({ data }) => setData(data)).catch(() => setData({ error: true }));
  }, [uid]);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose} data-testid="admin-user-modal">
      <motion.div initial={{ y: 30 }} animate={{ y: 0 }} exit={{ y: 30 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl border border-white/10 bg-[#0d0d18] p-6">
        {!data ? <Spinner label="Loading account trail" /> : data.error ? (
          <div className="text-red-300">Could not load user.</div>
        ) : (
          <>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="font-display text-xl font-bold text-white">{data.user.email}</div>
                <div className="text-xs text-slate-500">{data.user.name} · {data.user.role} · joined {data.user.created_at?.slice(0, 10)}</div>
              </div>
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-slate-400" data-testid="admin-user-modal-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              <Metric label="Balance" value={NUM(data.wallet.balance)} icon={Wallet} tone="gold" />
              <Metric label="Purchased" value={NUM(data.wallet.lifetime_purchased)} />
              <Metric label="Consumed" value={NUM(data.wallet.lifetime_consumed)} />
              <Metric label="Refunded" value={NUM(data.wallet.lifetime_refunded)} tone="green" />
            </div>

            <Section title={`Payments (${data.payments.length})`}>
              {data.payments.length === 0 ? <Empty t="No payments" /> : data.payments.map((p, i) => (
                <Row key={i} left={`${p.pack_id} · ${INR(p.amount_inr)}`} right={
                  <Pill tone={p.status === "paid" ? "green" : p.status === "created" ? "slate" : "red"}>{p.status}</Pill>
                } sub={`${p.order_id || ""} ${p.paid_at ? "· " + p.paid_at.slice(0, 19) : ""}`} />
              ))}
            </Section>

            <Section title={`Credit ledger (${data.credit_ledger.length})`}>
              {data.credit_ledger.slice(0, 30).map((l, i) => (
                <Row key={i} left={l.transaction_type} right={
                  <span className={cx("font-mono text-sm", l.credits >= 0 ? "text-emerald-300" : "text-slate-300")}>
                    {l.credits >= 0 ? "+" : ""}{NUM(l.credits)}
                  </span>
                } sub={`${l.reason || ""} · bal ${NUM(l.balance_after)} · ${(l.timestamp || "").slice(0, 19)}`} />
              ))}
            </Section>

            <Section title={`Generations (${data.generations.length})`}>
              {data.generations.slice(0, 30).map((g, i) => (
                <Row key={i} left={`${g.operation}${g.model ? " · " + g.model : ""}`} right={
                  <Pill tone={g.status === "failed" ? "red" : g.status?.startsWith("completed") ? "green" : "slate"}>{g.status}</Pill>
                } sub={`reserved ${NUM(g.credits_reserved)} · consumed ${NUM(g.credits_consumed)} · refunded ${NUM(g.credits_refunded)}${g.provider_cost_usd ? " · $" + g.provider_cost_usd : ""}`} />
              ))}
            </Section>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

const Section = ({ title, children }) => (
  <div className="mb-5">
    <div className="font-mono text-[11px] uppercase tracking-wider text-[#b6aaff] mb-2">{title}</div>
    <div className="rounded-xl border border-white/8 divide-y divide-white/5 overflow-hidden">{children}</div>
  </div>
);
const Row = ({ left, right, sub }) => (
  <div className="flex items-center justify-between px-4 py-2.5 bg-white/[0.02]">
    <div className="min-w-0"><div className="text-sm text-white truncate">{left}</div>{sub && <div className="text-[11px] text-slate-500 truncate">{sub}</div>}</div>
    <div className="shrink-0 ml-3">{right}</div>
  </div>
);
const Empty = ({ t }) => <div className="px-4 py-3 text-sm text-slate-500 bg-white/[0.02]">{t}</div>;

export default function AdminDashboard() {
  const { user } = useAuth();
  const [ov, setOv] = useState(null);
  const [users, setUsers] = useState(null);
  const [failed, setFailed] = useState([]);
  const [frm, setFrm] = useState("");
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");
  const [openUid, setOpenUid] = useState(null);
  const [gift, setGift] = useState(null); // {email}
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (frm) qs.set("frm", frm);
    if (to) qs.set("to", to);
    try {
      const [o, u, ff] = await Promise.all([
        api.get(`/admin/overview?${qs.toString()}`),
        api.get(`/admin/users?${qs.toString()}${q ? `&q=${encodeURIComponent(q)}` : ""}`),
        api.get(`/admin/failed-generations`),
      ]);
      setOv(o.data); setUsers(u.data.users); setFailed(ff.data.failed);
    } catch (e) {
      console.error("[Admin] load failed", e);
    } finally {
      setLoading(false);
    }
  }, [frm, to, q]);

  useEffect(() => { if (user?.role === "owner") load(); }, [user]); // eslint-disable-line

  if (user && user.role !== "owner") return <Navigate to="/" replace />;
  if (loading && !ov) return <Spinner label="Loading accountability data" />;
  const rev = ov?.revenue || {};
  const cost = ov?.cost || {};
  const cr = ov?.credits || {};

  return (
    <div data-testid="admin-dashboard">
      <StageHeader room="Owner · Accountability" icon={ShieldCheck}
        title="Studio Accountability"
        subtitle="Money in, AI cost out, credit flow and every generation — with full user investigation."
        actions={
          <button onClick={load} data-testid="admin-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white hover:border-[#5B4DFF]/50 transition">
            <RefreshCw className={cx("w-4 h-4", loading && "animate-spin")} /> Refresh
          </button>
        } />

      {/* Filters */}
      <GlassCard className="p-4 mb-6 flex flex-wrap items-end gap-3" data-testid="admin-filters">
        <label className="text-xs text-slate-400">From (ISO)
          <input value={frm} onChange={(e) => setFrm(e.target.value)} placeholder="2026-06-01"
            data-testid="admin-from" className="mt-1 block bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white w-40" />
        </label>
        <label className="text-xs text-slate-400">To (ISO)
          <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="2026-06-30"
            data-testid="admin-to" className="mt-1 block bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white w-40" />
        </label>
        <label className="text-xs text-slate-400 flex-1 min-w-[180px]">Find user (email)
          <div className="mt-1 flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5">
            <Search className="w-4 h-4 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="email contains…"
              data-testid="admin-user-search" className="bg-transparent outline-none text-sm text-white w-full" />
          </div>
        </label>
        <button onClick={load} data-testid="admin-apply"
          className="px-4 py-2 rounded-xl bg-[#5B4DFF] text-white text-sm font-semibold hover:bg-[#4B3DE6] transition">Apply</button>
      </GlassCard>

      {/* Money + margin */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Metric label="Customer Revenue" value={INR(rev.customer_revenue_inr)} sub={`${NUM(rev.paid_orders)} paid orders`} icon={IndianRupee} tone="green" />
        <Metric label="AI Provider Cost" value={INR(cost.actual_provider_inr)} sub={`est ${INR(cost.estimated_provider_inr)}`} icon={Cpu} />
        <Metric label="Gateway Cost" value={INR(rev.gateway_cost_inr)} sub={`@ ${(rev.gateway_fee_rate * 100).toFixed(2)}%`} icon={IndianRupee} tone="red" />
        <Metric label="Contribution Margin" value={INR(rev.contribution_margin_inr)} sub={rev.contribution_margin_pct != null ? `${rev.contribution_margin_pct}%` : "—"} icon={TrendingUp} tone="gold" />
      </div>

      {/* Users + credit flow */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Metric label="Total Users" value={NUM(ov?.users.total)} sub={`${NUM(ov?.users.active)} active in range`} icon={Users} />
        <Metric label="Credits Purchased" value={NUM(cr.purchased)} icon={Wallet} tone="green" />
        <Metric label="Credits Consumed" value={NUM(cr.consumed)} icon={Wallet} />
        <Metric label="Admin-Granted" value={NUM(cr.granted)} sub="₹0 revenue" icon={ShieldCheck} tone="gold" />
      </div>

      <div className="mb-2 text-[11px] text-slate-500 leading-relaxed">{ov?.note}</div>

      {/* Generation breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 my-6">
        <Metric label="Generations" value={NUM(ov?.generations.total)} sub={`${NUM(ov?.generations.failed)} failed`} icon={Cpu} tone={ov?.generations.failed ? "red" : "purple"} />
        <Metric label="Rate Card" value={ov?.rate_card_version || "—"} sub="versioned & configurable" icon={ShieldCheck} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <Breakdown title="By Status" rows={ov?.generations.by_status} />
        <Breakdown title="By Operation" rows={ov?.generations.by_operation} kind="credits" />
        <Breakdown title="By Model" rows={ov?.generations.by_model} kind="credits" />
        <Breakdown title="By Provider" rows={ov?.generations.by_provider} kind="credits" />
      </div>

      {/* Failed feed */}
      <GlassCard className="p-5 mb-6" data-testid="admin-failed-feed">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          <span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">Failed generations ({failed.length})</span>
        </div>
        {failed.length === 0 ? <div className="text-sm text-slate-500">No failed generations — all reservations refunded.</div> : (
          <div className="rounded-xl border border-white/8 divide-y divide-white/5 overflow-hidden">
            {failed.slice(0, 15).map((g, i) => (
              <Row key={i} left={`${g.operation}${g.model ? " · " + g.model : ""}`} right={<Pill tone="red">{g.error_code || "failed"}</Pill>}
                sub={`user ${g.user_id?.slice(-6)} · refunded ${NUM(g.credits_refunded)} · ${(g.failed_at || "").slice(0, 19)}`} />
            ))}
          </div>
        )}
      </GlassCard>

      {/* Users table */}
      <GlassCard className="p-5" data-testid="admin-users-table">
        <div className="font-mono text-[11px] uppercase tracking-wider text-slate-400 mb-3">Users — click to investigate</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-left text-slate-500 border-b border-white/8">
                <th className="py-2 font-medium">Email</th>
                <th className="py-2 font-medium">Balance</th>
                <th className="py-2 font-medium">Revenue</th>
                <th className="py-2 font-medium">Purchased</th>
                <th className="py-2 font-medium">Consumed</th>
                <th className="py-2 font-medium">Gens</th>
                <th className="py-2 font-medium">Plan</th>
                <th className="py-2 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {(users || []).map((u) => (
                <tr key={u.user_id} onClick={() => setOpenUid(u.user_id)}
                  data-testid={`admin-user-row-${u.email}`}
                  className="border-b border-white/5 hover:bg-white/[0.04] cursor-pointer transition">
                  <td className="py-2.5 text-white">{u.email} {u.role === "owner" && <Pill tone="gold" className="ml-1 !py-0">owner</Pill>}</td>
                  <td className="py-2.5 text-[#FFB300] font-mono">{NUM(u.balance)}</td>
                  <td className="py-2.5 text-emerald-300 font-mono">{INR(u.revenue_inr)}</td>
                  <td className="py-2.5 text-slate-400 font-mono">{NUM(u.lifetime_purchased)}</td>
                  <td className="py-2.5 text-slate-400 font-mono">{NUM(u.lifetime_consumed)}</td>
                  <td className="py-2.5 text-slate-400 font-mono">{u.generations}{u.failed ? ` (${u.failed}✗)` : ""}</td>
                  <td className="py-2.5">{u.watermark_free ? <Pill tone="green">wm-free</Pill> : <Pill tone="slate">tier {u.tier}</Pill>}</td>
                  <td className="py-2.5 text-right">
                    <button onClick={(e) => { e.stopPropagation(); setGift({ email: u.email }); }}
                      data-testid={`admin-gift-${u.email}`}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#5B4DFF]/15 border border-[#5B4DFF]/40 text-[#b6aaff] text-xs hover:bg-[#5B4DFF]/25 transition">
                      <Gift className="w-3.5 h-3.5" /> Gift
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GlassCard>

      <AnimatePresence>
        {openUid && <UserInvestigation uid={openUid} onClose={() => setOpenUid(null)} />}
        {gift && <GiftCredits email={gift.email} onClose={() => setGift(null)} onDone={load} />}
      </AnimatePresence>
    </div>
  );
}
